import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';

interface Payout {
  _id: string;
  artistId: string;
  periodMonth: string;
  activeSubscriberCount: number;
  amountPerSubscriber: number;
  totalAmount: number;
  status: 'pending' | 'paid' | 'voided';
  paidAt?: string;
}

function defaultMonth(): string {
  const d = new Date();
  d.setUTCMonth(d.getUTCMonth() - 1);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

export default function AffiliatePayouts() {
  const qc = useQueryClient();
  const [month, setMonth] = useState(defaultMonth());

  const { data } = useQuery<{ items: Payout[] }>({
    queryKey: ['admin', 'payouts', month],
    queryFn: () => api(`/api/v1/admin/affiliate-payouts/${month}`),
  });

  const recompute = useMutation({
    mutationFn: () =>
      api('/api/v1/admin/affiliate-payouts/recompute', {
        method: 'POST',
        body: JSON.stringify({ periodMonth: month }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'payouts'] }),
  });

  const markPaid = useMutation({
    mutationFn: (id: string) =>
      api(`/api/v1/admin/affiliate-payouts/${id}/mark-paid`, {
        method: 'POST',
        body: JSON.stringify({}),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'payouts'] }),
  });

  const total = (data?.items ?? []).reduce((s, p) => s + (p.status === 'pending' ? p.totalAmount : 0), 0);

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Affiliate payouts</h1>
        <div className="flex gap-2">
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="bg-surface border border-border rounded-md px-3 py-2"
          />
          <button
            onClick={() => recompute.mutate()}
            className="bg-surface2 border border-border px-4 py-2 rounded-md"
          >
            {recompute.isPending ? 'Recomputing…' : 'Recompute'}
          </button>
        </div>
      </div>
      <div className="bg-surface border border-border rounded-xl p-6 mb-6">
        <div className="text-text2 text-xs uppercase tracking-wide">Pending payout total ({month})</div>
        <div className="text-3xl font-bold text-accent mt-2">${total.toFixed(2)}</div>
      </div>
      <div className="bg-surface border border-border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-surface2 text-left text-text2">
            <tr>
              <th className="px-4 py-3">Artist</th>
              <th className="px-4 py-3 text-right">Subscribers</th>
              <th className="px-4 py-3 text-right">Per sub</th>
              <th className="px-4 py-3 text-right">Total</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {(data?.items ?? []).map((p) => (
              <tr key={p._id} className="border-t border-border">
                <td className="px-4 py-3 font-mono text-xs">{p.artistId}</td>
                <td className="px-4 py-3 text-right">{p.activeSubscriberCount}</td>
                <td className="px-4 py-3 text-right">${p.amountPerSubscriber}</td>
                <td className="px-4 py-3 text-right font-semibold text-accent">${p.totalAmount.toFixed(2)}</td>
                <td className="px-4 py-3">
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs ${
                      p.status === 'paid' ? 'bg-success text-bg' : p.status === 'pending' ? 'bg-accentDim text-bg' : 'bg-danger text-bg'
                    }`}
                  >
                    {p.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  {p.status === 'pending' ? (
                    <button
                      onClick={() => {
                        if (!confirm(`Mark $${p.totalAmount.toFixed(2)} as paid?`)) return;
                        markPaid.mutate(p._id);
                      }}
                      className="bg-accent text-bg px-3 py-1 rounded-md text-xs font-semibold"
                    >
                      Mark paid
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
